import { useEffect, useState } from 'react'
import { usePollen } from '@pilot-hub/ui'
import { Polygon } from 'react-leaflet'
import { api } from '@pilot-hub/api-client'


// Ebene „Abdeckung“: Anteil jedes Suchgebiets, der von der Kamera überflogen und gesehen wurde (Flugweg aus
// DJI Pilot 2, Berechnung in dji-sync). Standard: nur bis 60 m Höhe über Grund gilt als abgedeckt (Erkennbarkeit
// von Personen), einstellbar je Einsatz in der Taktischen Karte.

export const ABDECKUNG_STANDARD_HOEHE_M = 60

export interface AbdeckungGebiet {
  id: string | null
  kennung: string
  prozent: number
  flaeche_m2: number
  abgedeckt_m2: number
  /** Abgedeckte Rechtecke als [lat, lon]-Ringe (nur wenn Flächen angefordert wurden) */
  flaechen?: [number, number][][]
}

export interface Abdeckung {
  max_hoehe_m: number
  gebiete: AbdeckungGebiet[]
  annahmen: string[]
  zaehler: { punkte_gesamt: number; punkte_genutzt: number }
}

/** Abdeckung der Suchgebiete des Einsatzes, alle 10 s neu (Flächen nur bei eingeschalteter Ebene) */
export function useAbdeckung(einsatzId: string | number | null | undefined, maxHoehe: number, flaechen: boolean, aktiv: boolean) {
  const [daten, setDaten] = useState<Abdeckung | null>(null)
  useEffect(() => { setDaten(null) }, [einsatzId, maxHoehe, flaechen, aktiv])
  usePollen('Abdeckung', lebt => api.get(`/dji/abdeckung/${einsatzId}`, { params: { max_hoehe: maxHoehe, flaechen } })
    .then(r => { if (lebt()) setDaten(r.data as Abdeckung) }), 10000, [einsatzId, maxHoehe, flaechen], !!einsatzId && aktiv)
  return daten
}

export const abdeckungProzent = (daten: Abdeckung | null, sgId: string) => {
  const g = daten?.gebiete.find(x => x.id === sgId)
  return g && daten && daten.zaehler.punkte_genutzt > 0 ? g.prozent : null
}

/** Kartenebene (innerhalb von MapContainer): abgedeckte Fläche halbtransparent grün */
export function AbdeckungLayer({ daten }: { daten: Abdeckung | null }) {
  return (
    <>
      {(daten?.gebiete ?? []).filter(g => g.flaechen?.length).map(g => (
        <Polygon key={g.id ?? g.kennung} positions={g.flaechen!} interactive={false}
          pathOptions={{ stroke: false, fillColor: '#22c55e', fillOpacity: 0.4 }} />
      ))}
    </>
  )
}

/** Eingabe der maximalen Höhe (Erkennbarkeit von Personen) mit Hinweis auf die Annahmen */
export function AbdeckungHinweis({ daten }: { daten: Abdeckung | null }) {
  if (!daten) return null
  return (
    <details className="px-2 pb-1 text-xs text-dark-text-subtle">
      <summary className="cursor-pointer">Annahmen der Abdeckung</summary>
      <ul className="mt-1 list-disc space-y-0.5 pl-4">
        {daten.annahmen.map(a => <li key={a}>{a}</li>)}
        <li>{daten.zaehler.punkte_genutzt} von {daten.zaehler.punkte_gesamt} Flugweg-Punkten gewertet.</li>
      </ul>
    </details>
  )
}
